import { createContext, useContext, useState, useEffect } from "react";
import { db } from "../components/Auth/firebase";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { useAuth } from "./AuthContext";

const ProjectsContext = createContext();

export const useProjects = () => useContext(ProjectsContext);

export const ProjectsProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);

  useEffect(() => {
    if (!currentUser) return;

    const projectsCollectionRef = collection(db, "projects");
    const q = query(
      projectsCollectionRef,
      where("assignees", "array-contains", currentUser.uid)
    );

    const qForClients = query(
      projectsCollectionRef,
      where("clients", "array-contains", currentUser.uid)
    );

    const unsubscribeAssignees = onSnapshot(q, (snapshot) => {
      const assigneeProjectsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      mergeProjects(assigneeProjectsList);
    });

    const unsubscribeClients = onSnapshot(qForClients, (snapshot) => {
      const clientProjectsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      mergeProjects(clientProjectsList);
    });

    return () => {
      unsubscribeAssignees();
      unsubscribeClients();
    };
  }, [currentUser, activeProject]);

  const mergeProjects = (newProjects) => {
    setProjects((prevProjects) => {
      const projectMap = new Map();
      prevProjects.forEach((project) => projectMap.set(project.id, project));
      newProjects.forEach((project) => projectMap.set(project.id, project));
      return Array.from(projectMap.values());
    });
  };

  const deleteProject = async (projectId) => {
    await deleteDoc(doc(db, "projects", projectId));
  };

  return (
    <ProjectsContext.Provider
      value={{ projects, activeProject, setActiveProject, deleteProject }}
    >
      {children}
    </ProjectsContext.Provider>
  );
};
